import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, asList } from "../lib/api";
import { useAuth } from "./AuthContext";

const Ctx = createContext(null);
export const useAudio = () => useContext(Ctx);

export function AudioProvider({ children }) {
  const el = useRef(null);
  const graph = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const { user } = useAuth();
  const reloadTracks = useCallback(() => {
    api.get("/tracks").then((r) => setTracks(asList(r))).catch(() => setTracks([]));
  }, []);

  useEffect(() => { reloadTracks(); }, [user, reloadTracks]);

  const ensureGraph = () => {
    if (graph.current || !window.AudioContext) return;
    try {
      const ac = new window.AudioContext();
      const src = ac.createMediaElementSource(el.current);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.82;
      src.connect(analyser);
      analyser.connect(ac.destination);
      graph.current = { ac, analyser, data: new Uint8Array(analyser.frequencyBinCount) };
    } catch (e) {
      graph.current = null;
    }
  };

  const play = useCallback((track) => {
    const a = el.current;
    if (!a || !track?.audio) return;
    ensureGraph();
    graph.current?.ac?.resume();
    if (current?.slug !== track.slug) {
      a.src = track.audio;
      setCurrent(track);
      setTime(0);
      setDuration(track.duration || 0);
    }
    a.play().catch(() => setPlaying(false));
  }, [current]);

  const toggle = useCallback((track) => {
    const a = el.current;
    if (track && track.slug !== current?.slug) return play(track);
    if (!current) return play(track || tracks.find((t) => t.audio));
    if (a.paused) play(current);
    else a.pause();
  }, [current, play, tracks]);

  const step = useCallback((dir) => {
    const list = tracks.filter((t) => t.audio);
    if (!list.length) return;
    const i = list.findIndex((t) => t.slug === current?.slug);
    play(list[(i + dir + list.length) % list.length]);
  }, [tracks, current, play]);

  const seek = (t) => { if (el.current) el.current.currentTime = t; setTime(t); };

  const getLevels = () => {
    const g = graph.current;
    if (!g || !playing) return null;
    g.analyser.getByteFrequencyData(g.data);
    return g.data;
  };

  return (
    <Ctx.Provider value={{ tracks, current, playing, time, duration, play, toggle, step, seek, getLevels, reloadTracks }}>
      {children}
      <audio
        ref={el}
        crossOrigin="anonymous"
        preload="metadata"
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => step(1)}
      />
    </Ctx.Provider>
  );
}
